import {
    ChangeDetectionStrategy,
    Component,
    Input,
    ViewEncapsulation,
} from '@angular/core';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import * as React from 'react';
import { timer } from 'rxjs';

import { CmpsDecimalPipe } from '../common/number.pipe';
import {
    MetricDataItem,
    percentToTargetRanked,
} from '../performance-data/metric.pd.model';
import iocTypes from '../rvirus.imap';
import { lazyInject } from '../rvirus.ioc';

import { classNames } from './grid.insight.render';
import {
    SelectedVisualItemInsightProps,
    SelectedVisualItemInsightState,
} from './selected-visual-item.insight.model';

class SelectedVisualItemInsightImpl extends React.Component<
    SelectedVisualItemInsightProps,
    SelectedVisualItemInsightState
> {
    @lazyInject(iocTypes.CmpsDecimalPipe.identifier)
    private readonly _decimalPipe: CmpsDecimalPipe;

    constructor(props: SelectedVisualItemInsightProps) {
        super(props);

        this.state = { width: 0 };
    }

    componentDidMount(): void {
        this._setWidth();
    }

    componentDidUpdate(
        prevProps: Readonly<SelectedVisualItemInsightProps>,
        prevState: Readonly<SelectedVisualItemInsightState>,
        snapshot?: any,
    ): void {
        this._setWidth();
    }

    private _setWidth(): void {
        const pttR = !this.props.dataMetric
            ? null
            : percentToTargetRanked(
                  this.props.dataMetric.actual,
                  this.props.dataMetric.target,
                  this.props.dataMetric.rankOrder,
              );
        const width = !this.props.dataMetric ? 0 : Math.abs(pttR * 100);
        width !== this.state.width &&
            timer().subscribe(() => this.setState({ width }));
    }

    render(): React.ReactNode {
        return (
            <div>
                <div
                    className="mr-3"
                    data-pillar=""
                >
                    {this.props.pillarName}
                </div>
                <div className="insight-separator" />
                <div
                    className="mr-5"
                    data-metric=""
                >
                    {this.props.metricName}
                </div>
                {this.props.dataMetric && (
                    <div className="d-flex mr-5 cmps-cdk-align-items-center">
                        <div
                            className="insight-actual"
                            style={{
                                color:
                                    this.props.showThresholdColours &&
                                    this.props.dataMetric.targetThresholdColor,
                            }}
                        >
                            {!this.props.dataMetric
                                ? '-'
                                : this._decimalPipe.transform(
                                      this.props.dataMetric.actual,
                                      '1.1-2',
                                  )}
                        </div>
                        <div className="progress insight-gauge">
                            <div
                                className={`progress-bar insight-gauge-fill ${classNames(
                                    {
                                        'insight-metric-bold':
                                            this.props.showThresholdColours &&
                                            this.props.dataMetric
                                                .targetThresholdBold,
                                    },
                                )}`}
                                style={{
                                    width: `${this.state.width}%`,
                                    backgroundColor:
                                        this.props.showThresholdColours &&
                                        this.props.dataMetric
                                            .targetThresholdColor,
                                }}
                                role="progressbar"
                                aria-valuenow={this.state.width}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                        {this.props.showThresholdColours && (
                            <span className="sr-only">
                                {this.props.dataMetric.targetThresholdName}
                            </span>
                        )}
                        {this.props.showTrendArrows &&
                            this.props.dataMetric.trendClass?.split(' ')
                                ?.length > 1 && (
                                <>
                                    <div
                                        style={{
                                            color: this.props.dataMetric.trendClass.split(
                                                ' ',
                                            )[0],
                                        }}
                                        className="d-inline cmps-icon"
                                    >
                                        <svg>
                                            {this.props.dataMetric.trendClass
                                                .split(' ')[1]
                                                .toLowerCase()
                                                .includes('up') && (
                                                <path d={mdiChevronUp}></path>
                                            )}
                                            {this.props.dataMetric.trendClass
                                                .split(' ')[1]
                                                .toLowerCase()
                                                .includes('down') && (
                                                <path d={mdiChevronDown}></path>
                                            )}
                                        </svg>
                                    </div>
                                    <span className="sr-only">
                                        {this.props.dataMetric.trendName}
                                    </span>
                                </>
                            )}
                    </div>
                )}
            </div>
        );
    }
}

export const SelectedVisualItemInsight = SelectedVisualItemInsightImpl;

@Component({
    selector: '[rvirus][cmpsInsightSelectedVisualItem]',
    template: ` <cmps-insight-selected-visual-item
        [pillarName]="pillarName"
        [metricName]="metricName"
        [dataMetric]="dataMetric"
        [showThresholdColours]="showThresholdColours"
        [showTrendArrows]="showTrendArrows"
    ></cmps-insight-selected-visual-item>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class SelectedVisualItemInsightComponent {
    @Input() pillarName: string;
    @Input() metricName: string;
    @Input() dataMetric: MetricDataItem;
    @Input() showThresholdColours: boolean;
    @Input() showTrendArrows: boolean;
}
